import React, { useState, useEffect } from 'react';
import DashboardCard from '@/components/ui/DashboardCard';
import DashboardButton from '@/components/ui/DashboardButton';
import WorkbookListDnD from './WorkbookListDnD';
import { useWorkbook } from '@/contexts/workbook/WorkbookContext';
import { useAutoNavigation } from '@/hooks/nav/use-auto-navigation';
import { Folder, FilePlus2, Target } from 'lucide-react';
import GlassPill from '@/components/ui/GlassPill';
import { useModalTrigger } from '@/hooks/navigation/use-modal-trigger';
import { URL_PARAMS } from '@/config/url-parameters';
import { NewWorkbookForm, NewWorkbookFormState } from './NewWorkbookForm';
import { useToast } from '@/hooks/errors/use-toast';
import { useAIUsageData } from '@/hooks/subscription/useAIUsageData';
import { useGlobalModals } from '@/contexts/GlobalModalsContext';
import ProjectGenerationModal from './ProjectGenerationModal';
import { ProjectGenerationRequest } from '@/types/project-generation-types';

interface WorkbookMainContentProps {
  isLoading: boolean;
  workbookCount: number;
  shouldShowCenteredLayout: boolean;
}

const WorkbookMainContent: React.FC<WorkbookMainContentProps> = ({
  isLoading,
  workbookCount,
  shouldShowCenteredLayout
}) => {
  const { isCreateProjectModalOpen, closeCreateProjectModal, openCreateProjectModal } = useWorkbook();
  const { goToWorkbookEditor, goToTaskManager } = useAutoNavigation();
  
  // For centered layout - project creation form state
  const [centeredForm, setCenteredForm] = useState<NewWorkbookFormState>({ userGoal: "" });
  const [showCenteredValidation, setShowCenteredValidation] = useState(false);
  const [isGenerationModalOpen, setIsGenerationModalOpen] = useState(false);
  const [generationRequest, setGenerationRequest] = useState<ProjectGenerationRequest | null>(null);
  
  const { toast } = useToast();
  const usageData = useAIUsageData();
  const { openOutOfCreditsModal } = useGlobalModals();

  // URL parameter modal trigger detection
  const { shouldOpen: shouldOpenProjectModal } = useModalTrigger(URL_PARAMS.MODALS.CREATE_PROJECT);

  // Handle URL parameter trigger for project modal
  useEffect(() => {
    if (shouldOpenProjectModal) {
      console.log('[WorkbookMainContent] URL parameter detected, opening project modal');
      openCreateProjectModal();
    }
  }, [shouldOpenProjectModal, openCreateProjectModal]);

  const handleProjectCreated = (workbookId: string, goalId: string) => {
    // Navigate to the workbook editor when creating from project manager
    goToWorkbookEditor(workbookId);
    closeCreateProjectModal();
  };

  // Centered layout form handlers
  const handleCenteredSubmit = async () => {
    setShowCenteredValidation(true);
    if (!centeredForm.userGoal || centeredForm.userGoal.length < 10) return;

    // Credit check
    if (usageData.remainingBalance === 0) {
      console.log('[WorkbookMainContent] Insufficient credits - blocking project creation and showing modal');
      openOutOfCreditsModal();
      return;
    }

    console.log(`[WorkbookMainContent] Credits available (${usageData.remainingBalance}) - proceeding with project creation`);
    
    // Set up the generation request and open the progress modal
    setGenerationRequest({ userGoal: centeredForm.userGoal });
    setIsGenerationModalOpen(true);
  };

  const handleGenerationClose = () => {
    setIsGenerationModalOpen(false);
    setGenerationRequest(null);
  };

  const handleGenerationSuccess = (projectId: string, goalId: string) => {
    toast({
      title: "🎉 Project Created with AI!",
      description: "Your project has been created with AI-generated tasks and research recommendations.",
      variant: "success"
    });
    
    // Reset form and navigate
    setCenteredForm({ userGoal: "" });
    setShowCenteredValidation(false);
    goToTaskManager(goalId, projectId);
  };

  const isValid = centeredForm.userGoal.length >= 10;

  if (shouldShowCenteredLayout) {
    return (
      <>
        {/* Centered Search & Create form when no projects exist - using standardized EmptyStateLayout */}
        <div className="flex items-center justify-center min-h-[calc(100vh-200px)]">
          <div className="w-full max-w-2xl mx-auto px-4">
            <DashboardCard variant="purple" className="p-8 md:p-12">
              <div className="text-center mb-6">
                <div className="flex justify-center mb-4">
                  <div className="p-4 bg-gradient-to-br from-purple-500/20 to-blue-500/20 rounded-2xl">
                    <Target className="h-8 w-8 text-purple-500 dark:text-purple-400" />
                  </div>
                </div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Create Your First Project
                </h2>
                {/* Description */}
                <p className="text-center text-gray-600 dark:text-gray-400 text-sm mb-6">
                  Organize your work and collaborate with your team on structured projects
                </p>
              </div>
              
              {/* Centered Project Creation Form */}
              <div className="space-y-6">
                <NewWorkbookForm
                  value={centeredForm}
                  onChange={setCenteredForm}
                  showValidation={showCenteredValidation}
                />
                
                <div className="flex justify-center">
                  <button
                    onClick={handleCenteredSubmit}
                    disabled={!isValid}
                    className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-3 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    <div className="flex items-center gap-2">
                      <Target size={16} />
                      Create Project
                    </div>
                  </button>
                </div>
              </div>
            </DashboardCard>
          </div>
        </div>

        {/* Project Generation Progress Modal */}
        <ProjectGenerationModal
          isOpen={isGenerationModalOpen}
          onClose={handleGenerationClose}
          requestData={generationRequest}
          onSuccess={handleGenerationSuccess}
        />
      </>
    );
  }

  return (
    <div className="space-y-3 md:space-y-6">
      <DashboardCard className="p-6">
        {/* Header with Project Count and Desktop Button */}
        <div className="flex items-center justify-between mb-3 md:mb-4">
          <div className="flex items-center gap-2">
            <Folder className="text-purple-500 dark:text-purple-400" size={18} />
            <h3 className="font-medium text-base">Your Projects</h3>
            {workbookCount > 0 && (
              <GlassPill 
                type={workbookCount.toString()} 
                variant="purple" 
                className="ml-2 text-xs h-6 w-6 flex items-center justify-center p-0"
              />
            )}
          </div>
          
          {/* New Project Button - Desktop only */}
          <div className="hidden sm:block">
            <DashboardButton
              onClick={openCreateProjectModal}
              icon={FilePlus2}
              size="sm"
              className="h-8 px-3 py-1.5 text-sm"
            >
              New Project
            </DashboardButton>
          </div>
        </div>
        
        {/* Mobile New Project Button - Full width below header */}
        <div className="sm:hidden mb-4">
          <DashboardButton
            onClick={openCreateProjectModal}
            icon={FilePlus2}
            size="sm"
            className="h-8 px-3 py-1.5 text-sm w-full"
          >
            New Project
          </DashboardButton>
        </div>

        <WorkbookListDnD 
          parentIsLoading={isLoading}
          parentWorkbookCount={workbookCount}
          parentShowingCenteredLayout={shouldShowCenteredLayout}
        />
      </DashboardCard>
      

    </div>
  );
};

export default WorkbookMainContent; 